import React, { createContext, useContext, useEffect, useReducer } from 'react';
import ConfirmOverlayModal from '../components/modals/ConfirmOverlayModal';
import SmallPopupTop from '../components/modals/SmallPopupTop';
import SelectedOptionsModal from '../components/report/shared/SelectedOptionsModal';
import SiteAnnouncementBanner from '../components/modals/SiteAnnouncementBanner';
import { useUser } from './UserContext';
import { dismissAnnouncement, fetchActiveAnnouncements } from '../utils/api/announcementAPI';

const DEFAULT_SMALL_POPUP_DURATION = 5000;

const createSmallPopupTop = (payload) => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    message: payload.message,
    type: payload.type,
    title: payload.title,
    logText: payload.logText,
    durationMs: payload.persist ? null : (payload.durationMs ?? DEFAULT_SMALL_POPUP_DURATION),
});

const initialState = {
    confirmModal: {
        isOpen: false,
        message: '',
        subMessage: '',
        children: null,
        setDoNotShowAgain: null,
        onConfirm: () => { },
        onCancel: () => { }
    },
    selectedOptions: {
        isShowing: false,
        selected: [],
        total: 0,
        onDelete: () => { },
        onCancel: () => { }
    },
    siteAnnouncements: [],
    smallPopupTop: []
}

const reducer = (state, action) => {
    switch (action.type) {
        case 'SHOW_CONFIRM_MODAL':
            return {
                ...state,
                confirmModal: {
                    isOpen: true,
                    message: action.payload.message,
                    subMessage: action.payload.subMessage,
                    children: action.payload.children,
                    setDoNotShowAgain: action.payload.setDoNotShowAgain,
                    onConfirm: action.payload.onConfirm,
                    onCancel: action.payload.onCancel
                }
            }
        case 'RESET_CONFIRM_MODAL':
            return {
                ...state,
                confirmModal: {
                    isOpen: false,
                    message: '',
                    subMessage: '',
                    children: null,
                    setDoNotShowAgain: null,
                    onConfirm: () => { },
                    onCancel: () => { }
                }
            }
        case 'SHOW_SELECTED_OPTIONS':
            return {
                ...state,
                selectedOptions: {
                    isShowing: action.payload.selected > 0,
                    selected: action.payload.selected,
                    total: action.payload.total,
                    onDelete: action.payload.onDelete,
                    onCancel: action.payload.onCancel
                }
            }
        case 'SET_SITE_ANNOUNCEMENTS':
            return {
                ...state,
                siteAnnouncements: action.payload
            }
        case 'DISMISS_SITE_ANNOUNCEMENT':
            return {
                ...state,
                siteAnnouncements: state.siteAnnouncements.filter((announcement) => announcement.id !== action.payload.id)
            }
        case 'SHOW_SMALL_POPUP_TOP':
            return {
                ...state,
                smallPopupTop: [createSmallPopupTop(action.payload), ...state.smallPopupTop]
            }
        case 'REMOVE_SMALL_POPUP_TOP':
            return {
                ...state,
                smallPopupTop: state.smallPopupTop.filter((notice) => notice.id !== action.payload.id)
            }
        case 'RESET_OPTIONS': {
            const modalType = action.payload.modalType;
            if (modalType === 'smallPopupTop') {
                return {
                    ...state,
                    smallPopupTop: []
                };
            }

            return {
                ...state,
                [modalType]: {
                    ...initialState[modalType]
                }
            }
        }
        
        default:
            return state;
    }
}

const ModalContext = createContext();

export const ModalProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const { state: userState } = useUser();
    const userId = userState.user?._id || 'guest';

    useEffect(() => {
        let isMounted = true;

        const loadAnnouncements = async () => {
            try {
                const announcements = await fetchActiveAnnouncements(userId);
                if (isMounted) {
                    dispatch({ type: 'SET_SITE_ANNOUNCEMENTS', payload: announcements });
                }
            } catch (error) {
                console.error('Error fetching site announcements:', error);
                if (isMounted) {
                    dispatch({ type: 'SET_SITE_ANNOUNCEMENTS', payload: [] });
                }
            }
        };

        loadAnnouncements();

        const intervalId = window.setInterval(loadAnnouncements, 10 * 60 * 1000);

        return () => {
            isMounted = false;
            window.clearInterval(intervalId);
        };
    }, [userId]);

    const handleDismissAnnouncement = (announcement) => {
        dismissAnnouncement(announcement, userId);
        dispatch({ type: 'DISMISS_SITE_ANNOUNCEMENT', payload: { id: announcement.id } });
    };

    return (
        <ModalContext.Provider value={{ state, dispatch }}>
            {children}
            <div className="fixed top-4 inset-x-4 z-50 flex justify-center pointer-events-none">
                <div className="flex w-full max-w-3xl flex-col gap-3">
                    {state.siteAnnouncements.map((announcement) => (
                        <SiteAnnouncementBanner
                            key={announcement.id}
                            announcement={announcement}
                            onDismiss={() => handleDismissAnnouncement(announcement)}
                        />
                    ))}
                </div>
            </div>
            <ConfirmOverlayModal
                isOpen={state.confirmModal.isOpen}
                message={state.confirmModal.message}
                subMessage={state.confirmModal.subMessage}
                setDoNotShowAgain={state.confirmModal.setDoNotShowAgain}
                onCancel={() => {
                    if (state.confirmModal.onCancel) {
                        state.confirmModal.onCancel();
                    }
                    dispatch({ type: "RESET_CONFIRM_MODAL" });
                }}
                onConfirm={() => {
                    if (state.confirmModal.onConfirm) {
                        state.confirmModal.onConfirm();
                    }
                    dispatch({ type: "RESET_CONFIRM_MODAL" });
                }}
            >
                {state.confirmModal.children}
            </ConfirmOverlayModal>
            <div className="fixed top-6 right-3 z-40 flex w-full max-w-96 flex-col gap-3 pointer-events-none">
                {state.smallPopupTop.map((notice) => (
                    <SmallPopupTop
                        key={notice.id}
                        stacked
                        message={notice.message}
                        isShowing={true}
                        type={notice.type}
                        title={notice.title}
                        logText={notice.logText}
                        durationMs={notice.durationMs}
                        onDismiss={() => {
                            dispatch({ type: 'REMOVE_SMALL_POPUP_TOP', payload: { id: notice.id } });
                        }}
                    />
                ))}
            </div>
            <SelectedOptionsModal
                isShowing={state.selectedOptions.isShowing}
                selected={state.selectedOptions.selected}
                total={state.selectedOptions.total}
                onDelete={state.selectedOptions.onDelete}
                onCancel={state.selectedOptions.onCancel}

            />


        </ModalContext.Provider>
    )
}

export const useModal = () => useContext(ModalContext);